import { useState } from 'react';
import {
  useController,
  type Control,
  type UseFormSetValue,
  type UseFormWatch,
} from 'react-hook-form';
import { Box, Column, Row, Text } from '~/commonComponents';
import ConfirmPopup from '~/components/shared/ConfirmPopup';
import { UserProfession } from '~/enums/UserProfession';
import { Colors } from '~/themes/colors';
import { getIsAllowOfficeToPostProcedure } from '~/utils/DentistUtils';
import { AssistedHygieneField } from './fields/AssistedHygieneField';
import { ExpectedProceduresField } from './fields/ExpectedProcedures';
import { MealBreakDurationField } from './fields/MealBreakDurationField';
import { MealBreakPaidField } from './fields/MealBreakPaidField';
import { OfficeField } from './fields/OfficeField';
import { PreferredSpecialtyField } from './fields/PreferredSpecialtyField';
import { ProfessionalPayField } from './fields/ProfessionalPayField';
import { ProfessionalTypeField } from './fields/ProfessionalTypeField';
import { TimeFieldsWrapper } from './fields/TimeFieldsWrapper';
import { type IShiftDetailsFormValues } from './useShiftDetailsForm';

interface IShiftDetailsFormProps {
  control: Control<IShiftDetailsFormValues>;
  watch: UseFormWatch<IShiftDetailsFormValues>;
  setValue: UseFormSetValue<IShiftDetailsFormValues>;
  openField: string | null;
  toggleField: (field: string) => void;
}

const EdtBadge = () => (
  <Box
    backgroundColor={Colors.neutral_400}
    borderRadius={10}
    paddingHorizontal={6}
    height={15}
    alignItems="center"
    justifyContent="center"
  >
    <Text bold fontSize={12} color="white">
      EDT
    </Text>
  </Box>
);

export const ShiftDetailsForm = ({
  control,
  watch,
  setValue,
  openField,
  toggleField,
}: IShiftDetailsFormProps) => {
  const professionType = watch('professionType');
  const preferredSpecialty = watch('preferredSpecialty');
  const invite = watch('invite');
  const professionCode = professionType ? UserProfession.getCode(professionType) : '';
  const isRDH = professionCode === UserProfession.RDH;
  const showProcedures = getIsAllowOfficeToPostProcedure(professionType, preferredSpecialty);

  const { field: officeIdField } = useController({ name: 'officeId', control });
  const { field: professionTypeField } = useController({ name: 'professionType', control });
  const { field: preferredSpecialtyField } = useController({ name: 'preferredSpecialty', control });
  const { field: isAssistedHygieneField } = useController({ name: 'isAssistedHygiene', control });
  const { field: arrivalField } = useController({ name: 'arrivalTime', control });
  const { field: departureField } = useController({ name: 'departureTime', control });
  const { field: mealBreakDurationField } = useController({ name: 'mealBreakDuration', control });
  const { field: isMealBreakPaidField } = useController({ name: 'isMealBreakPaid', control });
  const { field: rateField } = useController({ name: 'rate', control });

  const [showOfficeChangeWarning, setShowOfficeChangeWarning] = useState(false);
  const [pendingOfficeId, setPendingOfficeId] = useState<string | null>(null);
  const [showProfessionMismatchWarning, setShowProfessionMismatchWarning] = useState(false);

  const handleOfficeChange = (newOfficeId: string) => {
    if (invite.length > 0 && newOfficeId !== officeIdField.value) {
      setPendingOfficeId(newOfficeId);
      setShowOfficeChangeWarning(true);
      return;
    }
    officeIdField.onChange(newOfficeId);
  };

  const handleProfessionTypeChange = (newProfessionType: string) => {
    if (invite.length > 0 && newProfessionType !== professionTypeField.value) {
      setShowProfessionMismatchWarning(true);
      return;
    }
    professionTypeField.onChange(newProfessionType);
  };

  return (
    <Column data-testid="shift-details-form" gap={20}>
      <OfficeField value={officeIdField.value} onChange={handleOfficeChange} />
      <ProfessionalTypeField
        value={professionTypeField.value}
        onChange={handleProfessionTypeChange}
        isOpen={openField === 'professionType'}
        onToggle={() => toggleField('professionType')}
      />
      <PreferredSpecialtyField
        value={preferredSpecialtyField.value}
        onChange={preferredSpecialtyField.onChange}
        isOpen={openField === 'preferredSpecialty'}
        onToggle={() => toggleField('preferredSpecialty')}
        professionType={professionType}
      />
      {showProcedures && (
        <>
          <Box height={1.5} backgroundColor={Colors.neutral_100} />
          <ExpectedProceduresField
            control={control}
            professionType={professionType}
            preferredSpecialty={preferredSpecialty}
          />
          <Box height={1.5} backgroundColor={Colors.neutral_100} />
        </>
      )}
      {isRDH && (
        <AssistedHygieneField
          value={isAssistedHygieneField.value}
          onChange={isAssistedHygieneField.onChange}
          isOpen={openField === 'isAssistedHygiene'}
          onToggle={() => toggleField('isAssistedHygiene')}
        />
      )}

      <Text bold fontSize={24} color="neutral_600" style={{ marginTop: 16 }}>
        Hours & Pay
      </Text>
      <Box height={1.5} backgroundColor={Colors.neutral_100} style={{ marginTop: -8 }} />

      <TimeFieldsWrapper
        arrivalValue={arrivalField.value}
        onArrivalChange={arrivalField.onChange}
        departureValue={departureField.value}
        onDepartureChange={departureField.onChange}
        openField={openField}
        toggleField={toggleField}
        labelRight={<EdtBadge />}
      />

      <Row gap={30}>
        <Column flex={1}>
          <MealBreakDurationField
            value={mealBreakDurationField.value}
            onChange={mealBreakDurationField.onChange}
            isOpen={openField === 'mealBreakDuration'}
            onToggle={() => toggleField('mealBreakDuration')}
          />
        </Column>
        <Column flex={1}>
          <MealBreakPaidField
            value={isMealBreakPaidField.value}
            onChange={isMealBreakPaidField.onChange}
            isOpen={openField === 'isMealBreakPaid'}
            onToggle={() => toggleField('isMealBreakPaid')}
          />
        </Column>
      </Row>

      <ProfessionalPayField
        value={rateField.value}
        onChange={rateField.onChange}
        isOpen={openField === 'rate'}
        onToggle={() => toggleField('rate')}
        professionType={professionType}
      />

      {showOfficeChangeWarning && (
        // @ts-expect-error -- ConfirmPopup still written in JavaScript
        <ConfirmPopup
          title="Confirm Office Change"
          description="Changing the office will clear the professionals you've selected to invite for this shift. Do you want to continue?"
          leftButtonText="Go Back"
          leftButtonAction={() => {
            setPendingOfficeId(null);
            setShowOfficeChangeWarning(false);
          }}
          rightButtonText="Continue"
          rightButtonAction={() => {
            officeIdField.onChange(pendingOfficeId);
            setValue('invite', []);
            setValue('inviteNames', {});
            setPendingOfficeId(null);
            setShowOfficeChangeWarning(false);
          }}
          closePopup={() => setShowOfficeChangeWarning(false)}
          dataTestId="office-change-popup"
          descriptionStyle={{
            width: '85%',
            marginBottom: 32,
          }}
        />
      )}

      {showProfessionMismatchWarning && (
        // @ts-expect-error -- ConfirmPopup still written in JavaScript
        <ConfirmPopup
          title="Professional Doesn't Match Shift Criteria"
          description="The selected professional does not match the required license type. Please verify the profession type and candidate before proceeding."
          rightButtonText="Close"
          rightButtonAction={() => setShowProfessionMismatchWarning(false)}
          closePopup={() => setShowProfessionMismatchWarning(false)}
          dataTestId="profession-mismatch-popup"
          descriptionStyle={{
            width: '85%',
            marginBottom: 32,
          }}
        />
      )}
    </Column>
  );
};
